import React, { useCallback, useEffect, useState } from 'react'
import { pricingManagementAdminService } from '../services/pricingManagementAdminService'
import { PageHeader } from '../components/ui/PageHeader'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { DataTable } from '../components/ui/DataTable'
import { Modal } from '../components/ui/Modal'

function formatMoney(n) {
  if (n == null || Number.isNaN(Number(n))) return '—'
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })
}

function formatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

const emptyForm = {
  tier: '',
  display_name: '',
  is_custom: false,
  base_price_per_credit: '',
  min_purchase_credit: '',
  is_active: true
}

const PricingManagement = () => {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [fieldErrors, setFieldErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const list = await pricingManagementAdminService.list(true)
      setRows(Array.isArray(list) ? list : [])
    } catch {
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const parseApiFieldErrors = (err) => {
    const errors = err?.response?.data?.errors
    if (!Array.isArray(errors)) return {}
    const next = {}
    errors.forEach((e) => {
      const key = (e.field || '').replace(/^body\./, '')
      if (key) next[key] = e.message
    })
    return next
  }

  const openAdd = () => {
    const hasCustom = rows.some((r) => r.is_custom)
    setEditingId(null)
    setFieldErrors({})
    setForm({
      ...emptyForm,
      is_custom: !hasCustom
    })
    setModalOpen(true)
  }

  const openEdit = (row) => {
    setEditingId(row.id)
    setFieldErrors({})
    setForm({
      tier: String(row.tier ?? ''),
      display_name: row.display_name ?? '',
      is_custom: Boolean(row.is_custom),
      base_price_per_credit: row.base_price_per_credit != null ? String(row.base_price_per_credit) : '',
      min_purchase_credit: row.min_purchase_credit != null ? String(row.min_purchase_credit) : '',
      is_active: Boolean(row.is_active)
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingId(null)
    setForm(emptyForm)
    setFieldErrors({})
  }

  const validateForm = () => {
    const err = {}
    const tier = parseInt(form.tier, 10)
    if (!Number.isFinite(tier) || tier < 0) err.tier = 'Tier must be a non-negative whole number'
    if (!String(form.display_name || '').trim()) err.display_name = 'Display name is required'
    const price = parseFloat(form.base_price_per_credit)
    if (!Number.isFinite(price) || price <= 0) err.base_price_per_credit = 'Enter a valid price per credit (USD)'
    const minCr = parseInt(form.min_purchase_credit, 10)
    if (!Number.isFinite(minCr) || minCr < 1) err.min_purchase_credit = 'Minimum credits must be at least 1'
    setFieldErrors(err)
    return Object.keys(err).length === 0
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setFieldErrors({})
    if (!validateForm()) return
    setSaving(true)
    try {
      const payload = {
        tier: parseInt(form.tier, 10),
        display_name: String(form.display_name).trim(),
        is_custom: form.is_custom,
        base_price_per_credit: parseFloat(form.base_price_per_credit),
        min_purchase_credit: parseInt(form.min_purchase_credit, 10),
        is_active: form.is_active
      }
      if (editingId) {
        await pricingManagementAdminService.update(editingId, payload)
      } else {
        await pricingManagementAdminService.create(payload)
      }
      closeModal()
      load()
    } catch (err) {
      setFieldErrors(parseApiFieldErrors(err))
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteRow = async (row) => {
    if (row.is_custom) return
    if (
      !window.confirm(
        `Delete pricing tier "${row.display_name}" (ID ${row.id})? This cannot be undone.`
      )
    ) {
      return
    }
    setDeletingId(row.id)
    try {
      await pricingManagementAdminService.destroy(row.id)
      await load()
    } catch {
      // errors surfaced via toast in service
    } finally {
      setDeletingId(null)
    }
  }

  const columns = [
    { key: 'id', header: 'ID', render: (row) => <span className="font-mono">{row.id}</span> },
    { key: 'tier', header: 'Tier' },
    { key: 'display_name', header: 'Name', render: (row) => <span className="font-medium">{row.display_name}</span> },
    {
      key: 'is_custom',
      header: 'Custom baseline',
      render: (row) => (row.is_custom ? <Badge variant="primary">Yes</Badge> : 'No'),
    },
    {
      key: 'base_price_per_credit',
      header: '$/credit',
      className: 'text-right',
      cellClassName: 'text-right font-mono',
      render: (row) => formatMoney(row.base_price_per_credit),
    },
    {
      key: 'min_purchase_credit',
      header: 'Min credits',
      className: 'text-right',
      cellClassName: 'text-right',
      render: (row) => row.min_purchase_credit?.toLocaleString?.() ?? row.min_purchase_credit,
    },
    {
      key: 'is_active',
      header: 'Active',
      render: (row) => (row.is_active ? <Badge variant="success">Yes</Badge> : <Badge>No</Badge>),
    },
    { key: 'updated_at', header: 'Updated', render: (row) => formatDate(row.updated_at) },
    {
      key: 'actions',
      header: 'Actions',
      className: 'text-right',
      cellClassName: 'text-right',
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button type="button" variant="link" onClick={() => openEdit(row)}>Edit</Button>
          {row.is_custom ? (
            <span className="text-xs text-text-muted" title="Cannot delete the custom baseline.">—</span>
          ) : (
            <Button type="button" variant="link" className="!text-red-600" onClick={() => handleDeleteRow(row)} disabled={deletingId === row.id}>
              {deletingId === row.id ? 'Deleting…' : 'Delete'}
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Pricing management"
        subtitle="Configure credit purchase tiers. Exactly one row must be the custom baseline (is_custom)."
        actions={
          <>
            <Button type="button" variant="secondary" onClick={() => load()} disabled={loading}>Refresh</Button>
            <Button type="button" onClick={openAdd} disabled={loading}>Add tier</Button>
          </>
        }
      />

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        emptyTitle="No pricing tiers"
        emptyDescription="Add a tier or run the backend seeder for pricing_management."
        getRowKey={(row) => row.id}
      />

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editingId != null ? `Edit pricing tier #${editingId}` : 'Add pricing tier'}
        size="md"
      >
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label htmlFor="pm-tier" className="block text-sm font-medium text-gray-700">
                  Tier
                </label>
                <input
                  id="pm-tier"
                  type="number"
                  min={0}
                  step={1}
                  value={form.tier}
                  onChange={(e) => setForm({ ...form, tier: e.target.value })}
                  className={`mt-1 w-full rounded-md border px-3 py-2 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                    fieldErrors.tier ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {fieldErrors.tier && <p className="mt-1 text-sm text-red-600">{fieldErrors.tier}</p>}
              </div>
              <div>
                <label htmlFor="pm-name" className="block text-sm font-medium text-gray-700">
                  Display name
                </label>
                <input
                  id="pm-name"
                  type="text"
                  value={form.display_name}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                  className={`mt-1 w-full rounded-md border px-3 py-2 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                    fieldErrors.display_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {fieldErrors.display_name && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.display_name}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="pm-custom"
                  type="checkbox"
                  checked={form.is_custom}
                  onChange={(e) => setForm({ ...form, is_custom: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <label htmlFor="pm-custom" className="text-sm font-medium text-gray-700">
                  Custom baseline (pay-as-you-go reference — only one row may have this)
                </label>
              </div>
              <div>
                <label htmlFor="pm-price" className="block text-sm font-medium text-gray-700">
                  Base price per credit (USD)
                </label>
                <input
                  id="pm-price"
                  type="text"
                  inputMode="decimal"
                  value={form.base_price_per_credit}
                  onChange={(e) => setForm({ ...form, base_price_per_credit: e.target.value })}
                  className={`mt-1 w-full rounded-md border px-3 py-2 font-mono shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                    fieldErrors.base_price_per_credit ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {fieldErrors.base_price_per_credit && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.base_price_per_credit}</p>
                )}
              </div>
              <div>
                <label htmlFor="pm-min" className="block text-sm font-medium text-gray-700">
                  Minimum purchase (credits)
                </label>
                <input
                  id="pm-min"
                  type="number"
                  min={1}
                  step={1}
                  value={form.min_purchase_credit}
                  onChange={(e) => setForm({ ...form, min_purchase_credit: e.target.value })}
                  className={`mt-1 w-full rounded-md border px-3 py-2 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                    fieldErrors.min_purchase_credit ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {fieldErrors.min_purchase_credit && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.min_purchase_credit}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="pm-active"
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <label htmlFor="pm-active" className="text-sm font-medium text-gray-700">
                  Active (inactive tiers are hidden from the public catalog)
                </label>
              </div>
              <div className="flex justify-end gap-2 border-t border-muted pt-4 dark:border-muted-dark">
                <Button type="button" variant="secondary" onClick={closeModal}>Cancel</Button>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving…' : editingId != null ? 'Save changes' : 'Create tier'}
                </Button>
              </div>
            </form>
      </Modal>
    </div>
  )
}

export default PricingManagement
